import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Skeleton, Tooltip } from 'antd';
import type { CampaignTimelineRow } from '@/models';
import type { TimelineViewValues } from '../../BookingSheet.types';
import { NAMESPACE } from '@/constants';
import { formatMoney, formatCompactMoney } from '@/constants/currency';
import { OTHER_CHANNEL, OTHER_COLOUR } from './channelColour';
import { TimelineCharts } from './TimelineCharts';
import { useTimelineModel, type TimelineFilters } from './useTimelineModel';
import {
  LABEL_WIDTH,
  Bar,
  BarTrack,
  BarValue,
  ChannelDot,
  EmptyState,
  FooterBar,
  Grid,
  GridCell,
  HeaderGutter,
  HeaderRow,
  HeaderStack,
  HeaderTrack,
  Layout,
  LegendItem,
  LegendRow,
  MajorCell,
  RowCampaign,
  RowLabel,
  RowMarks,
  RowMeta,
  RowText,
  StatusDot,
  SummaryFact,
  SummaryLine,
  TickCell,
  TimelineInner,
  TimelineRow,
  TimelineScroll,
  TodayFlag,
  TodayMarker,
  TooltipCard,
  TooltipRow,
  TooltipTitle
} from './CampaignTimeline.styles';

interface Props {
  rows: CampaignTimelineRow[];
  isLoading: boolean;
  isError: boolean;
  /** Driven by the page's single filter bar, so both tabs stay in step. */
  view: TimelineViewValues;
  /** Channel selection is the one filter the timeline owns: it is set by
   *  clicking the mix chart, which only exists here. */
  channel: string | null;
  onChannelSelect: (channel: string | null) => void;
  showCharts: boolean;
  /** Offered from the empty state, so a filter combination that matches
   *  nothing is not a dead end. */
  onResetFilters?: () => void;
}

/** Status hues are the reserved status tokens, never reused for a channel. */
const STATUS_COLOUR: Record<'live' | 'upcoming' | 'ended', string> = {
  live: '#1baf7a',
  upcoming: '#eda100',
  ended: '#8b8ba7'
};

/** How many flights are drawn before the rest are held behind "show more".
 *  The full set runs to thousands of rows; painting them all locks the tab. */
const PAGE_SIZE = 60;

export const CampaignTimeline: FC<Props> = ({
  rows,
  isLoading,
  isError,
  view,
  channel,
  onChannelSelect,
  showCharts,
  onResetFilters
}) => {
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);
  const [visible, setVisible] = useState(PAGE_SIZE);

  /* The page's search already narrowed these rows server-side, so the model's
     own term stays empty rather than filtering a second time. */
  const filters: TimelineFilters = useMemo(
    () => ({ ...view, channel, search: '' }),
    [view, channel]
  );

  // A changed filter means a different set of rows, so paging starts over
  // rather than leaving the reader partway down a list they have not seen.
  useEffect(() => setVisible(PAGE_SIZE), [filters]);

  const model = useTimelineModel(rows, filters);

  if (isError) {
    return <Alert type="error" role="alert" showIcon message={t('timelineLoadFailed')} />;
  }

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  const { bars, totals } = model;

  /* The axis gets the room its columns need rather than being squeezed into
     the viewport: a minimum per tick, plus the fixed label gutter. Below the
     comfortable floor the container simply fills the space it has. */
  const minTickWidth = filters.zoom === 'week' ? 34 : 52;
  const canvasWidth = Math.max(LABEL_WIDTH + model.ticks.length * minTickWidth, 820);
  const shown = bars.slice(0, visible);
  const avgDays = bars.length ? Math.round(totals.days / bars.length) : 0;

  const summary = (
    <SummaryLine>
      <SummaryFact>
        <strong>{bars.length.toLocaleString()}</strong>
        {t('timelineStatFlights').toLowerCase()}
      </SummaryFact>
      <SummaryFact>
        <strong>{formatCompactMoney(totals.net)}</strong>
        {t('timelineStatNet').toLowerCase()}
      </SummaryFact>
      <SummaryFact>
        <StatusDot $colour={STATUS_COLOUR.live} />
        <strong>{totals.live.toLocaleString()}</strong>
        {t('timelineStatusLive').toLowerCase()}
      </SummaryFact>
      <SummaryFact>
        <StatusDot $colour={STATUS_COLOUR.upcoming} />
        <strong>{totals.upcoming.toLocaleString()}</strong>
        {t('timelineStatusUpcoming').toLowerCase()}
      </SummaryFact>
      <SummaryFact>
        <StatusDot $colour={STATUS_COLOUR.ended} />
        <strong>{totals.ended.toLocaleString()}</strong>
        {t('timelineStatusEnded').toLowerCase()}
      </SummaryFact>
      <SummaryFact>
        <strong>{t('timelineDays', { count: avgDays })}</strong>
        {t('timelineStatAverage')}
      </SummaryFact>
    </SummaryLine>
  );

  if (!model.hasRows) {
    return (
      <EmptyState>
        {t('timelineEmpty')}
        {onResetFilters && (
          <Button size="small" onClick={onResetFilters}>
            {t('resetFilters')}
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <Layout>
      {summary}

      {showCharts && (
        <TimelineCharts
          volume={model.volume}
          channelMix={model.channelMix}
          zoom={filters.zoom}
          activeChannel={channel}
          onChannelSelect={(next) => onChannelSelect(channel === next ? null : next)}
        />
      )}

      {!bars.length ? (
        <EmptyState>
          {t('timelineNoneInRange')}
          {onResetFilters && (
            <Button size="small" onClick={onResetFilters}>
              {t('resetFilters')}
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <TimelineScroll>
            <TimelineInner $minWidth={canvasWidth}>
              <HeaderStack>
                <HeaderRow>
                  <HeaderGutter>{t('timelineCampaign')}</HeaderGutter>
                  <HeaderTrack>
                    {model.majorTicks.map((tick) => (
                      <MajorCell key={tick.key} $weight={tick.weight}>
                        {tick.label}
                      </MajorCell>
                    ))}
                  </HeaderTrack>
                </HeaderRow>
                <HeaderRow>
                  <HeaderGutter $muted>
                    {t('timelineShowingOf', {
                      shown: shown.length,
                      total: bars.length
                    })}
                  </HeaderGutter>
                  <HeaderTrack>
                    {model.ticks.map((tick) => (
                      <TickCell key={tick.key} $weight={tick.weight} $major={tick.major}>
                        {tick.label}
                      </TickCell>
                    ))}
                  </HeaderTrack>
                </HeaderRow>
              </HeaderStack>

              {shown.map((bar) => {
                const { row } = bar;
                const meta = [row.channel, row.platform, row.trader].filter(Boolean).join(' · ');
                const range = `${bar.start.format('DD MMM YYYY')} — ${bar.end.format(
                  'DD MMM YYYY'
                )}`;
                /* A zero net is printed as nothing rather than "£0.00": these
                   are measurement and non-media lines carrying no spend, and a
                   money label on every one of them is noise that crowds out
                   the values that do mean something. */
                const net =
                  typeof row.net === 'number' && row.net !== 0 ? formatMoney(row.net, 'GBP') : null;
                // The label is printed past the bar, or flipped inside the left
                // edge when the bar runs too close to the right of the chart.
                const flip = bar.left + bar.width > 78;
                const statusLabel = t(
                  `timelineStatus${bar.status[0].toUpperCase()}${bar.status.slice(1)}`
                );

                return (
                  <TimelineRow key={bar.key}>
                    <RowLabel>
                      <RowMarks>
                        <ChannelDot $colour={bar.colour} title={row.channel ?? ''} />
                        <StatusDot $colour={STATUS_COLOUR[bar.status]} title={statusLabel} />
                      </RowMarks>
                      <RowText>
                        <RowCampaign title={row.campaign ?? ''}>{row.campaign || '—'}</RowCampaign>
                        <RowMeta title={meta}>{meta || '—'}</RowMeta>
                      </RowText>
                    </RowLabel>

                    <BarTrack>
                      <Grid aria-hidden>
                        {model.ticks.map((tick, index) => (
                          <GridCell
                            key={tick.key}
                            $weight={tick.weight}
                            $major={tick.major}
                            $alt={index % 2 === 1}
                          />
                        ))}
                      </Grid>

                      {model.todayLeft !== null && <TodayMarker $left={model.todayLeft} />}

                      <Tooltip
                        destroyTooltipOnHide
                        title={
                          <TooltipCard as="div">
                            <TooltipTitle>{row.campaign || '—'}</TooltipTitle>
                            <TooltipRow>
                              {t('timelineTipDates')} <strong>{range}</strong>
                            </TooltipRow>
                            <TooltipRow>
                              {t('timelineTipDuration')}{' '}
                              <strong>{t('timelineDays', { count: bar.days })}</strong>
                            </TooltipRow>
                            {meta && (
                              <TooltipRow>
                                {t('timelineTipMedia')} <strong>{meta}</strong>
                              </TooltipRow>
                            )}
                            {net && (
                              <TooltipRow>
                                {t('net')} <strong>{net}</strong>
                              </TooltipRow>
                            )}
                            <TooltipRow>
                              {t('timelineTipLineItems')} <strong>{row.line_items ?? 0}</strong>
                            </TooltipRow>
                          </TooltipCard>
                        }
                      >
                        <Bar
                          $left={bar.left}
                          $width={bar.width}
                          $colour={bar.colour}
                          $dim={bar.status === 'ended'}
                          $clipStart={bar.clippedStart}
                          $clipEnd={bar.clippedEnd}
                          $unpriced={!net}
                          role="img"
                          aria-label={`${row.campaign ?? ''}, ${range}, ${statusLabel}, ${
                            net ?? t('timelineNoSpend')
                          }`}
                        />
                      </Tooltip>

                      {net && bar.width < 62 && (
                        <BarValue $left={flip ? bar.left : bar.left + bar.width} $flip={flip}>
                          {net}
                        </BarValue>
                      )}
                    </BarTrack>
                  </TimelineRow>
                );
              })}

              {model.todayLeft !== null && (
                <HeaderRow aria-hidden>
                  <HeaderGutter $muted />
                  <BarTrack style={{ height: 22 }}>
                    <TodayFlag $left={model.todayLeft}>{t('timelineToday')}</TodayFlag>
                  </BarTrack>
                </HeaderRow>
              )}
            </TimelineInner>
          </TimelineScroll>

          <FooterBar>
            <LegendRow as="div">
              {model.channels.slice(0, 8).map((name) => (
                <LegendItem
                  key={name}
                  type="button"
                  $active={channel === name}
                  aria-pressed={channel === name}
                  onClick={() => onChannelSelect(channel === name ? null : name)}
                >
                  <ChannelDot $colour={model.colourOf(name)} />
                  {name}
                </LegendItem>
              ))}
              {model.hasOther && (
                <LegendItem type="button" $active={false} disabled>
                  <ChannelDot $colour={OTHER_COLOUR} />
                  {OTHER_CHANNEL}
                </LegendItem>
              )}
            </LegendRow>

            {shown.length < bars.length && (
              <Button size="small" onClick={() => setVisible((n) => n + PAGE_SIZE)}>
                {t('timelineShowMore', { count: Math.min(PAGE_SIZE, bars.length - shown.length) })}
              </Button>
            )}
          </FooterBar>
        </>
      )}
    </Layout>
  );
};
